import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import styles from './Form.module.scss';
import Actions from '../actions/Actions';
import { useStudentNextContent } from '../../useStudentNextContent';
import { FeedbackForm } from '@types';
import { isHomeEDApp } from '@constants';
import { getMediaAbsolutePath } from '@services/File';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '@components/spinner/Spinner';
import Button from '@components/button/Button';
import { useStudentLessonContent } from '@src/hooks/useStudentLessonContent';
import FeedbackFormScroll from './FeedbackFormScroll';
import { useSubmitFeedbackFormAnswerForAbsentMutation } from '@src/api/graphql-codegen';
import { useParams } from 'react-router-dom';
import { logger } from '../../../../utils/logger';
// Use singleton worker to prevent destruction by React StrictMode

type Props = {
  data?: string | '';
  pageNumber?: number | 0;
  formData?: string | null;
  isFullScreen: boolean;
  id: string;
  name: string;
  isPreview?: boolean;
  goBack?: () => void;
  studyPlanId?: string;
  changePageNumber: (value: number) => void;
  startLesson?: () => void;
};

type Params = { id: string };

function Form(props: Props) {
  const {data, pageNumber, formData, isFullScreen, goBack, isPreview, changePageNumber} = props;
  
  // Initialize PDF worker singleton

  const {hasNextContent, selectPrev, selectNext} = useStudentNextContent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const {totalPages, setTotalPages, showExitTicket, setShowExitTicket} = useStudentLessonContent();

  const { id } = useParams<Params>();

  const [submitFeedbackFormAnswerForAbsent, { loading }] = useSubmitFeedbackFormAnswerForAbsentMutation();
  // show slide data
  useEffect(() => {
    if (data) {
      setShowExitTicket(false);
      // For slides.com templates, set totalPages to 1 since they're treated as a single page
      if (data.includes('slidescom')) {
        setTotalPages(1);
      } else {
        setTotalPages(0);
      }
    } else {
      setShowExitTicket(true);
    }
  }, [data, setShowExitTicket, setTotalPages]);

  const fileObj = useMemo(() => {
    if (data) {
      return getMediaAbsolutePath(data);  
    }
    return null;    
  }, [data]);

  const iframeSrc = useMemo(() => {
    return isPreview ? `${props.data}/index-clear.html` : `${props.data}/index-black.html`;
  }, [isPreview, props.data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
  }, [setTotalPages]);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    setTimeout(calculateSize, 50);
  }, [calculateSize]);

  const prevDisabled = useMemo(() => {
    return pageNumber ? pageNumber <= 1 : false
  }, [pageNumber]);

  const isLast = useMemo(() => {
    return pageNumber ? pageNumber === totalPages : false
  }, [pageNumber, totalPages]);

  const handleLeft = useCallback(() => {
    if (pageNumber) {
      changePageNumber(Math.max(pageNumber - 1, 1));
      if (!prevDisabled) setShowExitTicket(false);
    } else {
      selectPrev();
    }
  }, [pageNumber, prevDisabled, setShowExitTicket, changePageNumber, selectPrev]);

  const handleRight = useCallback(() => {
    if (pageNumber) {
      changePageNumber(Math.min(pageNumber + 1, totalPages));
    }
    if (isLast) {
      setShowExitTicket(true);
    }
  }, [totalPages, pageNumber, changePageNumber, isLast, setShowExitTicket]);
  
  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    if (!data) return null;
    return <Button size={'extraSmall'} onClick={handleLeft} label={'Previous'}/>;
  }, [data, prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (showExitTicket) return null;
    if (!data) return null;
    return <Button size={'extraSmall'} onClick={handleRight} label={'Next'}/>;
  }, [data, showExitTicket, handleRight]);
  //

  const jsonFormData = useMemo(() => {
    if (!formData) return null;
    try {
      return JSON.parse(formData) as FeedbackForm;
    } catch(e) {
      logger.info('parse error-', e, formData);
      return null
    }
  }, [formData]);

  const exitTicket = useMemo(() => (jsonFormData ? true : false), [jsonFormData]);
  const showMarkCompleteModal = useMemo(() => {
    return (exitTicket && !hasNextContent) ? true : false
  }, [exitTicket, hasNextContent])

  const SubmitExitTicketAnswer = useCallback(async (formDataWithAnswers: string) => {
    await submitFeedbackFormAnswerForAbsent({
      variables: {
        input: {
          link: id,
          formData: formDataWithAnswers
        }
      }
    });
    selectNext();
  }, [id, selectNext, submitFeedbackFormAnswerForAbsent])

  const ratio = useMemo(() => {
    if (width && height) {
      return width/height
    }
    return 1
  }, [width, height])

  if (!jsonFormData) return null;
  return (
    <Actions fullScreen={isFullScreen} leftButton={prevButton} rightButton={nextButton} totalPages={totalPages} pageNumber={pageNumber} className={styles.actionsWrapper} hideNextButton={isHomeEDApp || (showMarkCompleteModal && !hasNextContent)} goBack={goBack} exitTicket={exitTicket} isPreview={isPreview} formData={formData}>
      {data && !showExitTicket ? <div className={`${styles.slideWrapper} ${!isPreview ? styles.fullScreen : ''}`} ref={wrapperRef}>
        <div className={isFullScreen ? styles.fullContent : styles.content}>
          {data && data.includes('slidescom') ? (
            <div
              style={{
                width: `${width}px`,
                height: `${height}px`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <iframe
                src={iframeSrc}
                title="Zip Slide"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            </div>
          ) : (
            <Document
              file={fileObj}
              onLoadSuccess={onDocumentLoadSuccess}
              className={styles.document}
              loading={<Spinner/>}
            >
              <Page loading={<Spinner/>} pageNumber={pageNumber} width={ratio > 1.7 ? 0 : width} 
                    height={height}/>
            </Document>
          )}
        </div>
      </div> :
      <div className={styles.scrollView}>
        <FeedbackFormScroll onSubmit={SubmitExitTicketAnswer} form={jsonFormData} loading={loading} />
      </div>
      }
    </Actions>
  );
}

export default Form;
